import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable, fromEvent, merge, of } from 'rxjs';
import { map, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Network } from '@capacitor/network';
import { PlatformService } from '../utils/platform.service';
import { Injectable, NgZone, Inject, PLATFORM_ID } from '@angular/core';
import { APP_CONFIG } from '../config/app.config';

/** Au-delà, la connexion est affichée « lente ». */
const SLOW_LATENCY_MS = 3_000;
/** Sonde abandonnée au-delà : serveur injoignable malgré un réseau « connecté ». */
const PROBE_TIMEOUT_MS = 8_000;
/** Re-sonde périodique (premier plan uniquement) : une 4G dégradée n'émet aucun événement. */
const PROBE_INTERVAL_MS = 5 * 60_000;

export enum NetworkStatus {
  ONLINE = 'online',
  OFFLINE = 'offline',
  SLOW = 'slow',
}

export interface ConnectionQuality {
  status: NetworkStatus;
  latencyMs: number | null;
  lastChecked: number;
}

/**
 * Service de détection et monitoring de l'état réseau
 * - Utilise @capacitor/network pour une meilleure compatibilité mobile
 * - Détecte les changements online/offline
 * - Teste la qualité de connexion
 */
@Injectable({
  providedIn: 'root',
})
export class NetworkService {
  private readonly networkStatusSubject = new BehaviorSubject<NetworkStatus>(NetworkStatus.ONLINE);
  public readonly networkStatus$: Observable<NetworkStatus> = this.networkStatusSubject.asObservable().pipe(distinctUntilChanged());

  private readonly onlineSubject = new BehaviorSubject<boolean>(true);
  public readonly isOnline$: Observable<boolean> = this.onlineSubject.asObservable().pipe(distinctUntilChanged());

  private readonly connectionQualitySubject = new BehaviorSubject<ConnectionQuality>({
    status: NetworkStatus.ONLINE,
    latencyMs: null,
    lastChecked: Date.now(),
  });
  public readonly connectionQuality$: Observable<ConnectionQuality> = this.connectionQualitySubject.asObservable().pipe(distinctUntilChanged((a, b) => a.status === b.status && a.latencyMs === b.latencyMs));

  constructor(
    private ngZone: NgZone,
    privateplatformService: PlatformService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    if (isPlatformBrowser(this.platformId)) {
      this.onlineSubject.next(navigator.onLine);
      this.initializeNetworkListeners();
      this.checkConnectionQuality();
      setInterval(() => {
        if (document.visibilityState === 'visible' && navigator.onLine) void this.checkConnectionQuality();
      }, PROBE_INTERVAL_MS);
    }
  }

  /**
   * Initialiser les écouteurs d'événements réseau
   */
  private async initializeNetworkListeners(): Promise<void> {
    // Écouteur Capacitor (fonctionne sur Web et Native)
    try {
      // État initial
      const status = await Network.getStatus();
      this.updateStatus(status.connected);

      // Écouter les changements
      Network.addListener('networkStatusChange', (status) => {
        this.ngZone.run(() => {
          this.updateStatus(status.connected);

          if (status.connected) {
            this.checkConnectionQuality();
          }
        });
      });
    } catch (error) {
      console.error('Network plugin error, falling back to window events', error);

      // Fallback sur les événements window standard
      merge(
        fromEvent(window, 'online'),
        fromEvent(window, 'offline')
      )
        .pipe(debounceTime(500))
        .subscribe(() => {
          this.updateStatus(navigator.onLine);
          if (navigator.onLine) this.checkConnectionQuality();
        });
    }
  }

  private updateStatus(isOnline: boolean) {
    this.onlineSubject.next(isOnline);
    this.networkStatusSubject.next(isOnline ? NetworkStatus.ONLINE : NetworkStatus.OFFLINE);
  }

  /**
   * Vérifier la qualité de connexion
   */
  private async checkConnectionQuality(): Promise<void> {
    if (!this.onlineSubject.value) {
      this.networkStatusSubject.next(NetworkStatus.OFFLINE);
      this.connectionQualitySubject.next({ status: NetworkStatus.OFFLINE, latencyMs: null, lastChecked: Date.now() });
      return;
    }

    // Sonde sur l'API : les assets de l'APK sont locaux et ne mesurent pas le réseau.
    const controller = new AbortController();
    const abortTimer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
    const startTime = Date.now();

    try {
      await fetch(`${APP_CONFIG.apiUrl}${APP_CONFIG.apiPath}/global-config/app-status?t=${startTime}`, {
        method: 'GET',
        cache: 'no-store',
        signal: controller.signal,
      });

      const latency = Date.now() - startTime;
      const status = latency > SLOW_LATENCY_MS ? NetworkStatus.SLOW : NetworkStatus.ONLINE;

      this.networkStatusSubject.next(status);
      this.connectionQualitySubject.next({ status, latencyMs: latency, lastChecked: Date.now() });

      if (status === NetworkStatus.SLOW) {
        console.warn('⚠️ Slow network detected:', latency, 'ms');
      }
    } catch (error) {
      // Connecté mais serveur injoignable : c'est précisément la situation « 4G saturée ».
      console.warn('❌ Network connectivity check failed:', error);
      this.networkStatusSubject.next(NetworkStatus.SLOW);
      this.connectionQualitySubject.next({ status: NetworkStatus.SLOW, latencyMs: null, lastChecked: Date.now() });
    } finally {
      clearTimeout(abortTimer);
    }
  }

  /**
   * Obtenir le statut actuel (synchrone)
   */
  get isOnline(): boolean {
    return this.onlineSubject.value;
  }

  /**
   * Obtenir le statut réseau actuel (synchrone)
   */
  get networkStatus(): NetworkStatus {
    return this.networkStatusSubject.value;
  }

  get connectionQuality(): ConnectionQuality {
    return this.connectionQualitySubject.value;
  }

  async refreshConnectionQuality(): Promise<ConnectionQuality> {
    await this.checkConnectionQuality();
    return this.connectionQualitySubject.value;
  }
}
